import { observer } from 'mobx-react-lite'
import { ComponentProps, FC, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../../../hooks/useAuth'
import { useMediaQuery } from '../../../hooks/useMediaQuery'
import { segmentButtonClick } from '../../../lib/segment'
import { styled } from '../../../lib/stitches'
import { IProfile } from '../../../stores/registry/ProfileRegistry'
import { AuthFlowModal } from '../../AuthFlow/AuthFlowModal'
import Avatar from '../../core/Avatar'
import { Box, Column, Row } from '../../core/Components'
import DropdownMenu from '../../core/DropdownMenu'
import { ChevronDownIcon, GearIcon, LogoutIcon, QuestionMarkCicleIcon } from '../../core/icons'
import Skeleton from '../../core/Skeleton'
import Text from '../../core/Text/Text'
import { EditProfileFlowModal } from '../../EditProfileFlowModal/EditProfileFlowModal'
import Notifications from '../../profile/Notifications'
import { gray11, gray15, gray3, gray9 } from '../color'
import { PUBLIC_NOTION_URL } from '../constants'

const Card = styled(Box, {
  background: '$background__primary',
  boxShadow: '0px 1px 20px 0px #1018280D',
  padding: '3px',
  borderRadius: '$round',
  border: `1px solid ${gray3}`,
  cursor: 'pointer',
  fontSize: '$12',
  lineHeight: '$18',
  color: '$text__secondary',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'all 100ms ease',
  '&:hover': { transform: 'translateY(-1px)' },
  '&:active': { transform: 'translateY(0px)' }
})

const ChevronDown = styled(ChevronDownIcon, {
  marginLeft: '$4',
  marginRight: '$4'
})

const ProfileContent = styled('div', {
  display: 'flex',
  '> div:first-of-type': {
    marginRight: '15px'
  },
  variants: {
    hideCardText: {
      true: {
        position: 'static',
        '> div:first-of-type': {
          marginRight: '10px'
        }
      }
    }
  }
})

interface ProfileCardDropdownMenuContent {
  user: IProfile
  logout: () => void
  openEditProfileModal: () => void
}

export const ProfileCardDropdownMenuContent: FC<ProfileCardDropdownMenuContent> = ({
  user,
  logout,
  openEditProfileModal
}) => (
  <>
    <Link
      to={`/profile/${user.wallet_address}`}
      style={{ paddingBottom: '8px', paddingLeft: '15px', paddingRight: '15px' }}
      onClick={() => {
        segmentButtonClick('See Profile')
      }}
    >
      <Row verticalCenter>
        <Avatar imageUrl={user.profile_picture} size="47px" css={{ marginRight: '15px' }} />
        <Column>
          <Text css={{ color: gray15, fontSize: '16px', fontWeight: 700 }}>
            {user.display_name}
          </Text>
          <Text css={{ color: gray9, '&:hover': { textDecoration: 'underline' } }}>
            See your profile
          </Text>
        </Column>
      </Row>
    </Link>
    <DropdownMenu.Separator />
    <DropdownMenu.Item
      css={{ backgroundColor: 'transparent', border: 'none' }}
      onClick={() => {
        openEditProfileModal()
        segmentButtonClick('Edit Profile')
      }}
    >
      <GearIcon style={{ marginRight: '12px' }} />
      Edit profile
    </DropdownMenu.Item>
    <a href={PUBLIC_NOTION_URL} target="_blank" rel="noreferrer">
      <DropdownMenu.Item
        onClick={() => {
          segmentButtonClick('Support')
        }}
      >
        <QuestionMarkCicleIcon style={{ marginRight: '12px' }} />
        Support
      </DropdownMenu.Item>
    </a>
    <DropdownMenu.Item onClick={logout}>
      <LogoutIcon style={{ marginRight: '12px' }} />
      Log out
    </DropdownMenu.Item>
  </>
)

interface Props extends ComponentProps<typeof Card> {
  hideCardText?: boolean
  large?: boolean
  hideDropdown?: boolean
}

const ProfileCard: FC<Props> = observer((props) => {
  const { user, logout, isUserLoading } = useAuth()
  const [isEditProfileModalOpen, setIsEditProfileModalOpen] = useState(false)

  const picture: string = user?.profile_picture
  const name: string = user?.display_name

  if (!user) {
    let trigger
    if (isUserLoading) {
      trigger = (
        <Card {...props}>
          <Skeleton css={{ borderRadius: '$round', width: '24px', height: '24px' }} />
          {!props.hideCardText && (
            <Skeleton
              css={{
                borderRadius: '$round',
                width: '50px',
                height: '12px',
                marginLeft: '$8'
              }}
            />
          )}
        </Card>
      )
    } else {
      trigger = (
        <Card
          {...props}
          onClick={() => {
            segmentButtonClick('Log In')
          }}
        >
          <Text
            size={'14'}
            css={{ margin: '4px 10px', fontWeight: '$medium', color: gray11, textAlign: 'center' }}
          >
            Log In
          </Text>
        </Card>
      )
    }
    return <AuthFlowModal trigger={<Box>{trigger}</Box>} />
  }

  const card = (
    <Card {...props} css={{}}>
      <Avatar size={props.large ? '40px' : '24px'} imageUrl={picture} />
      {!props.hideCardText && (
        <>
          <Text
            size={props.large ? '20' : '14'}
            type={'secondary'}
            css={{ marginLeft: '6px', fontWeight: '$medium' }}
          >
            {name}
          </Text>
          {!props.hideDropdown && <ChevronDown />}
        </>
      )}
    </Card>
  )

  if (props.hideDropdown) {
    return (
      <ProfileContent>
        <Notifications />
        {card}
      </ProfileContent>
    )
  }

  return (
    <ProfileContent hideCardText={props.hideCardText}>
      <Notifications />
      <DropdownMenu.Body {...props} trigger={card}>
        <ProfileCardDropdownMenuContent
          user={user}
          logout={logout}
          openEditProfileModal={() => setIsEditProfileModalOpen(true)}
        />
      </DropdownMenu.Body>
      <EditProfileFlowModal
        open={isEditProfileModalOpen}
        onOpenChange={(value) => setIsEditProfileModalOpen(value)}
      />
    </ProfileContent>
  )
})

export const TopRightProfileCard: FC<ComponentProps<typeof Card> & Props> = (props) => {
  const isLessThanTabletScreen = useMediaQuery(960)

  if (isLessThanTabletScreen) {
    return null
  }

  return (
    <Box
      css={{
        ...props.css,
        position: 'absolute',
        right: '32px',
        top: '32px',
        marginBottom: '$24',
        zIndex: 2
      }}
    >
      <ProfileCard {...props} />
    </Box>
  )
}

export default ProfileCard
